<template>
  <div class="container-page">
    <div>
      <p class="title-inner">Участники МПК</p>

      <div class="mb-16" v-if="canEdit">
        <ButtonComponent mod="gradient-bg" @click="editCard(0)">Создать</ButtonComponent>
      </div>

      <div class="mb-4">
        <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
      </div>

      <div>
        <div
          class="table-container mb-16 js-table-arrow"
          :class="{ 'table-container-overflow': page?.items?.length < 4 }"
        >
          <table class="stockTable">
            <thead>
              <tr>
                <th>Страна</th>
                <th>ФИО</th>
                <th>Организация</th>
                <th>Должность</th>
                <th>Контактный телефон</th>
                <th>Адрес электронной почты</th>
                <th>Категория</th>
                <th v-if="canEdit"></th>
                <th v-if="canEdit"></th>
              </tr>
              <tr>
                <th class="filter">
                  <SelectMultiComponent
                    class="multiselect-min-width"
                    v-model="filter.countryId"
                    :options="countries"
                    mode="single"
                    :async="false"
                    placeholder="Выберите"
                    :canClear="true"
                  />
                </th>
                <th class="filter">
                  <FilterInput v-model="filter.name" placeholder="Введите" :useNewClearButton="true" />
                </th>
                <th class="filter">
                  <SelectMultiComponent
                    class="multiselect-min-width"
                    v-model="filter.organizationId"
                    :options="organizations"
                    mode="single"
                    :async="false"
                    placeholder="Выберите"
                    :canClear="true"
                  />
                </th>
                <th class="filter">
                  <FilterInput v-model="filter.position" placeholder="Введите" :useNewClearButton="true" />
                </th>
                <th class="filter"></th>
                <th class="filter"></th>
                <th class="filter">
                  <SelectMultiComponent
                    class="multiselect-min-width"
                    v-model="filter.participantCategoryId"
                    :options="categories"
                    mode="single"
                    :async="false"
                    placeholder="Выберите"
                    :canClear="true"
                  />
                </th>
                <th v-if="canEdit" class="filter"></th>
                <th v-if="canEdit" class="filter"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!page.items || !page.items.length">
                <td class="text-center" colspan="9">Нет данных.</td>
              </tr>
              <tr v-else v-for="item in page.items" :key="item.id" @dblclick="editCard(item.id)">
                <td>{{ item.country.name }}</td>
                <td class="d-flex align-items-center">
                  <div
                    v-if="item.picture"
                    class="imagePreviewWrapper rounded50 mr-8"
                    :style="{ 'background-image': `url(${item.picture})` }"
                  ></div>
                  {{ item.name }}
                </td>
                <td>{{ item.organization?.name }}</td>
                <td>{{ item.position }}</td>
                <td>{{ item.phone }}</td>
                <td>{{ item.email }}</td>
                <td>{{ item.participantCategory.name }}</td>
                <td v-if="canEdit">
                  <IconComponent @click="editCard(item.id)" name="mode" />
                </td>
                <td v-if="canEdit">
                  <IconComponent @click="deleteCard(item.id)" name="delete" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="page.pagesTotal > 1 && page.items && page.items.length">
          <Pagination
            :pageCount="page.pagesTotal"
            :currentPage="page.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { debounce } from 'lodash';

  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import FilterInput from '@/common/components/FilterInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import IconComponent from '@/common/ui/IconComponent.vue';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import API from '../api/participant';

  import { DIRECTIVES_ACTIONS_NAME, DIRECTIVES_GETTERS_NAME } from '@/store/directives';

  export default {
    name: 'Participants',
    components: {
      ButtonComponent,
      IconComponent,
      DefaultFilter,
      FilterInput,
      SelectMultiComponent,
    },
    mixins: [security],
    data() {
      return {
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        filter: {
          countryId: null,
          name: null,
          organizationId: null,
          position: null,
          participantCategoryId: null,
        },
        page: {},
        canEdit: false,
        organizations: [],
        categories: [],
      };
    },
    computed: {
      ...mapGetters({
        getPartnerCountrySearchIdList: DIRECTIVES_GETTERS_NAME.getPartnerCountrySearchIdList,
      }),

      countries() {
        return (
          this.getPartnerCountrySearchIdList?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || []
        );
      },
    },
    created() {
      this.checkPermissionAndRedirect(Constants.Permissions.MpkCommissionAccess);
      this.checkPermission(Constants.Permissions.MpkCommissionEdit).then((response) => {
        this.canEdit = response.data;
      });

      this.getPartnerCountrySearchId();

      loadFilter(this.$options.name).then((response) => {
        let filter = response;
        if (filter) {
          this.filter = filter;
        }
        this.loadDictionaries();
        this.loadPage(false);
      });
    },
    watch: {
      filter: {
        handler() {
          this.debouncedChangeFilter();
        },
        deep: true,
      },
    },
    methods: {
      ...mapActions({
        getPartnerCountrySearchId: DIRECTIVES_ACTIONS_NAME.getPartnerCountrySearchId,
      }),

      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      loadPage(append) {
        let request = Object.assign({}, Object.assign(this.request, this.filter));

        Object.keys(request).forEach((key) => {
          if (request[key] === null || request[key] === '' || request[key] === undefined) {
            delete request[key];
          }
        });

        API.search(request)
          .then((response) => {
            if (!append) {
              this.page = response.data || {};
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
          })
          .catch(() => {
            this.page = {};
          });
      },
      loadDictionaries() {
        API.getOrganizationList()
          .then((response) => {
            this.organizations = response.data.items.map((item) => ({ value: item.id, label: item.name }));
          })
          .catch(() => {
            this.organizations = [];
          });

        API.getCategoryList()
          .then((response) => {
            this.categories = response.data.items.map((item) => ({ value: item.id, label: item.name }));
          })
          .catch(() => {
            this.categories = [];
          });
      },
      editCard(id) {
        this.$router.push({ name: 'ParticipantCard', params: { id: id, action: this.canEdit ? 'edit' : 'view' } });
      },
      deleteCard(id) {
        Constants.alert.fire(Constants.confirmOptions.delete).then((res) => {
          if (res.isConfirmed) {
            API.delete(id)
              .then((response) => {
                console.log(response);
                Constants.alert.fire('Удаление', 'Запись удалена', 'success');
                this.loadPage();
              })
              .catch((error) => {
                Constants.alert.fire(
                  'Удаление',
                  error.response && error.response.data ? error.response.data : 'Запись не удалена',
                  'error',
                );
              });
          }
        });
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      debouncedChangeFilter: debounce(function () {
        this.changeFilter();
      }, 500),
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../../assets/theme/default/colors.scss';
  .table-container-overflow {
    overflow: visible;
  }

  .imagePreviewWrapper {
    width: 50px;
    height: 50px;
    display: block;
    cursor: pointer;
    background-size: cover;
    background-position: center center;
  }

  .multiselect-min-width {
    min-width: 110px;
  }

  .container-page {
    ::v-deep .input-group-wrapper {
      .form-label,
      input:not([type='checkbox']):not([type='time']),
      .select2 .select2-selection__rendered {
        font-size: 12px;
        border-radius: 4px;
        border-color: #d6e4ec;
        color: $gray-silver-dark;
        padding-left: 14px;
        padding-right: 14px;
        min-height: 40px !important;
      }
      .clear-button {
        align-self: flex-start;
        margin-top: 12px;
      }
    }
  }
</style>
